import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import UTIF from "utif";
import { Row, Col, Container } from "reactstrap";
import {
  GlyphInfo,
  TooltipButton,
  Modal,
  Hint,
  ProgressCircle,
} from "@americanexpress/dls-react";
import "./FileUploader.css";
import dragIcon from "../../assets/drag-icon.svg";
import remove from "../../assets/Group 658.svg";
import closeIcon from "../../assets/closeIcon.svg";
import { IEC_CODES } from "../Common/constants";
import { getNormalizedCountry, getPAScreeningCountries, getUploaderLabel } from "../Common/utils";
import { base64ToBlob } from "../../util/uploadUtils";

const PdfPreview = React.memo(({ base64Content }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    const blob = base64ToBlob(base64Content);
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [base64Content]);

  if (!blobUrl) return null;

  return (
    <iframe
      src={blobUrl}
      height="200px"
      width="100%"
      title="pdf-preview"
      style={{ border: "none" }}
    />
  );
});

const TiffPreview = React.memo(({ base64Content }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!base64Content || !canvasRef.current) return;

    const base64Data = base64Content.split(",")[1];
    const binary = atob(base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const ifds = UTIF.decode(bytes.buffer);
    UTIF.decodeImage(bytes.buffer, ifds[0]);
    const rgba = UTIF.toRGBA8(ifds[0]);

    const canvas = canvasRef.current;
    canvas.width = ifds[0].width;
    canvas.height = ifds[0].height;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.createImageData(ifds[0].width, ifds[0].height);
    imageData.data.set(rgba);
    ctx.putImageData(imageData, 0, 0);
  }, [base64Content]);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "auto", display: "block" }} />;
});

const FileUploader = (props) => {
  const { t, i18n } = useTranslation("common");
  const [tipModal, toggleTip] = useState(false);
  let uploaderClasses = "file-uploader";
  if (props.dragging) {
    uploaderClasses += " file-uploader--dragging";
  }
  const { onSelectFileClick, files, removeFile, country, isModifyUser, isLoading } = props;

  const uploaderLabel = getUploaderLabel(country, t, isModifyUser);

  const isScreeningCountries = getPAScreeningCountries(t).includes(country);

  const countryNorm = getNormalizedCountry(country);
  const isIec = IEC_CODES.map((code) => getNormalizedCountry(code)).includes(countryNorm);

  const fileName =
    files?.length > 0 ? (
      files.map((item, index) => {
        const safeKey = `${item.documentName || 'file'}-${index}`;
        return (
          <div className="file-item" key={safeKey}>
            <div className="file-item__header">
              <span className="file-item__name">Document Name: {item.documentName}</span>
              <button type="button" className="file-item__remove-btn" onClick={() => removeFile(index)}>
                <img src={remove} alt="remove Icon" />
              </button>
            </div>
            <div className="file-item__preview">
              {item.mimeType === "image/tiff" ? (
                <TiffPreview base64Content={item.base64Content} />
              ) : item.mimeType === "application/pdf" ? (
                <PdfPreview base64Content={item.base64Content} />
              ) : (
                <img
                  src={item.base64Content}
                  alt={item.documentName}
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              )}
            </div>
          </div>
        );
      })
    ) : (
      <img className="file-uploader__empty-icon" src={dragIcon} alt="drag Icon" />
    );
  return (<>
    <Container fluid className="Outercontainer">
      <div className="uploadDiv">
        {isScreeningCountries ? <p><strong>{uploaderLabel}</strong></p> :
          <p>
            {t("document.upload1")}
            <br />
            {t("document.upload2")}
          </p>
        }

        {isIec && i18n.language === "en" && (
          <p>{t("document.upload.supportingDocumentGuidanceIEC")}</p>
        )}

        {!isScreeningCountries && <span className="uploadTooltip">
          <TooltipButton
            icon={<GlyphInfo />}
            onClick={() => toggleTip(!tipModal)}
            tabIndex="-1"
          />
        </span>}
      </div>

      {tipModal ? (
        <Modal className="tooltipModal">
          <div className="tooltipBody">
            <div className="closebutton">
              <button onClick={() => toggleTip(!tipModal)}>
                <img src={closeIcon} alt="close Icon" width="10" />
              </button>
            </div>
            <div className="bgTool">
              <h3>
                Documents required for adding Authorised Signatory to this
                Control Account:
              </h3>
              <ul>
                <li>
                  Resolution of the Board of Directors / Power of Attorney
                  (accompanied with Board Resolution) to transact business on
                  Company's behalf (signed by any of the Directors or Company
                  Secretary). If the document signer is the same the person
                  being added as the Authorized Signatory, then the Board
                  Resolution needs to be counter signed by another
                  Director/Company Secretary. This implies that such a Board
                  Resolution needs to be signed by 2 people in the
                  aforementioned scenario. Signatures must have the name of the
                  signer and designation below the signature
                </li>
                <li>
                  . Copy of a self-attested valid photo identification and
                  address proof of the individuals who is being added as an
                  Authorized Signatory
                </li>
                <li>
                  . Copy of any ID proof with Date of Birth. Copy of
                  self-attested PAN Card.
                </li>
              </ul>
              <h3>Acceptable documents as Date of Birth proof:</h3>
              <ul>
                <li>
                  PAN Card (should be supplemented with an address proof listed
                  below
                </li>
                <li>
                  ) Driving License (DL) (DL issued by Government of Maharashtra
                  post 2011 is not acceptable as address proof. DL issued by
                  Govt. of NCT of Delhi post 2012 is not acceptable as address
                  proof. Learner's license is not acceptable
                </li>
                <li>). Election / Voter's ID Card </li>
                <li> Passport </li>
                <li>
                  Copy of AADHAAR Card issued by Unique Identification Authority
                  of India(UIDAI)/ *E - AADHAAR
                  <p>
                    E - AADHAAR can be accepted as an ID / Address proof by way
                    of downloading a copy of E - AADHAAR only when the applicant
                    is physically present in the Delhi branch (Saket) (An ID
                    Proof necessarily needs to contain the complete Date of
                    Birth of the applicant, and wherever the same is missing a
                    supplement document with the same must be obtained).
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </Modal>
      ) : null}

      <Container
        fluid
        className={uploaderClasses}
        onDrag={props.onDrag}
        onDragStart={props.onDragStart}
        onDragEnd={props.onDragEnd}
        onDragOver={props.onDragOver}
        onDragEnter={props.onDragEnter}
        onDragLeave={props.onDragLeave}
        onDrop={props.onDrop}
        onSelectFileClick={props.onSelectFileClick}
      >
        {isLoading && (
          <div className="file-uploader__loader">
            <ProgressCircle />
          </div>
        )}
        <Row className="file-uploader__contents">
          <Col xs="12" sm="12" md="12" lg="12" xl="12">
            {fileName}
          </Col>
        </Row>
        <br />
        <Row className="file-uploader__import">
          <Col xs="12" sm="12" md="12" lg="12" xl="12">
            <div>{t("document.drag")}</div>
            <button type="button" className="file-uploader__Btn" onClick={onSelectFileClick}>
              {t("document.browse")}
            </button>
            <div>
              {t("document.maxFile")}
              <br />
              {t("document.supportedFiles")}
            </div>
            {props.children}
          </Col>
        </Row>
      </Container>
    </Container>

      {isIec && i18n.language === "en" && (
        <>
          <Hint className="legal-1 pad-2-b">
            {t("newUserUK.iecDocInfo1")}
            <br />
            {isModifyUser ? (
              <>
                - {t("newUserUK.nameInfoIEC")} {t("newUserUK.addressInfoIEC")}
                <br />
                - {t("newUserUK.modifySupportingDocumentGuidanceIEC")}
              </>
            ) : (
              <>
                {t("newUserUK.iecDocInfo2")}
                <br />
                - {t("newUserUK.modifySupportingDocumentGuidanceIEC")}
              </>
            )}
          </Hint>
        </>
      )}
  </>
  );
};

export default FileUploader;
